// Shared chart primitives for the BOXX operator console.
//
// Every colour comes from theme.ts. Every chart re-reads the theme mode, draws a
// recessive grid, ships a legend the moment there are two or more series, and
// keeps status colours out of the series palette. The rules those choices come
// from are written at the top of theme.ts — read that first.

import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";
import { motion } from "framer-motion";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BLUE_RAMP,
  CHROME,
  INK,
  ORDINAL_RAMP,
  SERIES,
  SURFACE,
  rampAt,
  readMode,
  seriesColor,
  type Mode,
} from "./theme";

// ============================================================ theme plumbing

// App.tsx stamps data-theme on <html> and publishes no context for it, so the
// charts watch the attribute themselves. Without this a theme toggle would leave
// every already-mounted chart painted in the old mode's ink.
function useMode(): Mode {
  const [mode, setMode] = useState<Mode>(readMode);
  useEffect(() => {
    const sync = () => setMode(readMode());
    const obs = new MutationObserver(sync);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
    const mq = window.matchMedia?.("(prefers-color-scheme: light)");
    mq?.addEventListener("change", sync);
    return () => {
      obs.disconnect();
      mq?.removeEventListener("change", sync);
    };
  }, []);
  return mode;
}

// ============================================================ formatting

const COMPACT = new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 });
const FULL = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });

const compact = (n: number) => (Number.isFinite(n) ? COMPACT.format(n) : "–");
const full = (n: number) => (Number.isFinite(n) ? FULL.format(n) : "–");
const pct = (n: number) => `${(n * 100).toFixed(n >= 0.995 || n === 0 ? 0 : 1)}%`;

const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

// ============================================================ chrome helpers

const axisTick = (mode: Mode) => ({ fill: INK[mode].muted, fontSize: 11 });

function gridProps(mode: Mode, vertical: boolean) {
  // Hairline, solid, one step off the surface. Dashed gridlines read as noise.
  return { stroke: CHROME[mode].grid, strokeWidth: 1, vertical, horizontal: !vertical } as const;
}

function axisLine(mode: Mode) {
  return { stroke: CHROME[mode].axis, strokeWidth: 1 } as const;
}

function yAxisLabel(text: string | undefined, mode: Mode) {
  if (!text) return undefined;
  return {
    value: text,
    angle: -90,
    position: "insideLeft" as const,
    style: { fill: INK[mode].muted, fontSize: 11, textAnchor: "middle" as const },
  };
}

// ============================================================ tooltip

interface TipItem {
  name?: string | number;
  value?: number | string;
  color?: string;
  dataKey?: string | number;
}
interface TipProps {
  active?: boolean;
  label?: string | number;
  payload?: TipItem[];
}

/** Values lead, labels follow: the number is the strong element, the series name is secondary. */
function ChartTooltip({
  active,
  label,
  payload,
  mode,
  keyShape = "line",
  format = full,
}: TipProps & { mode: Mode; keyShape?: "line" | "rect"; format?: (n: number) => string }) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <motion.div
      className="tooltip"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.09 }}
    >
      {label !== undefined && label !== "" && <div className="t-label">{String(label)}</div>}
      {payload.map((p, i) => (
        <div className="t-row" key={`${String(p.dataKey ?? p.name ?? i)}`}>
          <span
            className="swatch"
            style={{
              width: 10,
              height: keyShape === "line" ? 3 : 10,
              borderRadius: keyShape === "line" ? 2 : 3,
              background: p.color,
              flex: "none",
            }}
          />
          <span>{String(p.name ?? "")}</span>
          <span className="val">{typeof p.value === "number" ? format(p.value) : String(p.value ?? "–")}</span>
        </div>
      ))}
    </motion.div>
  );
}

// Recharts clones the `content` element with its own prop shape. Handing it a
// callback that accepts `unknown` and casting once keeps this file free of `any`
// and free of recharts' internal generics.
const tipContent =
  (mode: Mode, keyShape: "line" | "rect", format?: (n: number) => string) => (p: unknown) => (
    <ChartTooltip {...(p as TipProps)} mode={mode} keyShape={keyShape} format={format} />
  );

// ============================================================ shared frame

interface LegendEntry {
  label: string;
  color: string;
  value?: string;
}

function Legend({ entries, shape }: { entries: LegendEntry[]; shape: "line" | "rect" }) {
  return (
    <div className="legend">
      {entries.map((e) => (
        <span className="item" key={e.label}>
          <span className={shape === "line" ? "swatch line" : "swatch"} style={{ background: e.color }} />
          <span>{e.label}</span>
          {e.value !== undefined && <span className="value">{e.value}</span>}
        </span>
      ))}
    </div>
  );
}

function ChartFrame({
  title,
  note,
  children,
  legend,
  legendShape = "line",
}: {
  title?: string;
  note?: string;
  children: ReactNode;
  legend?: LegendEntry[];
  legendShape?: "line" | "rect";
}) {
  return (
    <div className="chart">
      {(title || note) && (
        <div className="chart-head">
          {title && <h3>{title}</h3>}
          {note && <span className="note">{note}</span>}
        </div>
      )}
      {children}
      {/* One series is named by the title; a one-swatch legend just restates it. */}
      {legend && legend.length > 1 && <Legend entries={legend} shape={legendShape} />}
    </div>
  );
}

function NoData({ height, body }: { height: number; body?: string }) {
  return (
    <div className="empty" style={{ height, padding: "var(--sp-5)" }}>
      <h3>No data</h3>
      {body && <p>{body}</p>}
    </div>
  );
}

// ============================================================ line chart

export interface SeriesPoint {
  x: string | number;
  y: number | null;
}
export interface ChartSeries {
  name: string;
  points: SeriesPoint[];
}
export interface LineChartCardProps {
  title: string;
  series: ChartSeries[];
  yLabel?: string;
  note?: string;
}

type MergedRow = Record<string, string | number | null>;

/**
 * Time series. One y-axis, always — two measures of different scale are two
 * charts, never a second axis.
 */
export function LineChartCard({ title, series, yLabel, note }: LineChartCardProps) {
  const mode = useMode();
  const H = 260;

  const rows = useMemo<MergedRow[]>(() => {
    const byX = new Map<string, MergedRow>();
    const order: string[] = [];
    for (const s of series) {
      for (const p of s.points) {
        const k = String(p.x);
        if (!byX.has(k)) {
          byX.set(k, { x: p.x });
          order.push(k);
        }
        byX.get(k)![s.name] = p.y;
      }
    }
    const out = order.map((k) => byX.get(k)!);
    // Numeric x arrives out of order often enough (bucketed latencies, hour
    // offsets) that sorting it is worth the check; string x keeps insertion order
    // because that is the caller's chronology.
    if (out.length > 0 && out.every((r) => typeof r.x === "number")) {
      out.sort((a, b) => (a.x as number) - (b.x as number));
    }
    return out;
  }, [series]);

  const drawn = series.filter((s) => s.points.length > 0);
  const colors = new Map(drawn.map((s) => [s.name, seriesColor(s.name, mode)]));
  const single = drawn.length === 1;

  const allValues = rows.flatMap((r) =>
    drawn.map((s) => r[s.name]).filter((v): v is number => typeof v === "number"),
  );
  const nonNegative = allValues.every((v) => v >= 0);

  // Direct end-labels only survive when the lines separate at the right edge.
  // Converging lines get nudged labels that detach from their marks, so past a
  // 6%-of-range gap we fall back to the legend and the tooltip.
  const ends = drawn.map((s) => {
    for (let i = rows.length - 1; i >= 0; i--) {
      const v = rows[i][s.name];
      if (typeof v === "number") return { name: s.name, index: i, value: v };
    }
    return null;
  });
  const span = allValues.length ? Math.max(...allValues) - Math.min(...allValues) : 0;
  const endYs = ends.filter((e): e is { name: string; index: number; value: number } => !!e).map((e) => e.value);
  const collides =
    span > 0 &&
    endYs.some((a, i) => endYs.some((b, j) => i !== j && Math.abs(a - b) < span * 0.06));
  const showEndLabels = drawn.length <= 4 && !collides && rows.length > 1;

  if (rows.length === 0 || drawn.length === 0) {
    return (
      <ChartFrame title={title} note={note}>
        <NoData height={H} body="No points in the selected range." />
      </ChartFrame>
    );
  }

  const legend: LegendEntry[] = drawn.map((s) => ({ label: s.name, color: colors.get(s.name)! }));

  return (
    <ChartFrame title={title} note={note} legend={legend} legendShape="line">
      <ResponsiveContainer width="100%" height={H}>
        <ComposedChart data={rows} margin={{ top: 8, right: showEndLabels ? 74 : 12, bottom: 4, left: yLabel ? 8 : 0 }}>
          <CartesianGrid {...gridProps(mode, false)} />
          <XAxis
            dataKey="x"
            tick={axisTick(mode)}
            tickLine={false}
            axisLine={axisLine(mode)}
            minTickGap={16}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={axisTick(mode)}
            tickLine={false}
            axisLine={false}
            width={48}
            domain={nonNegative ? [0, "auto"] : ["auto", "auto"]}
            tickFormatter={compact}
            label={yAxisLabel(yLabel, mode)}
          />
          {/* The crosshair finds the X; readers aim at a bucket, not a 2px line. */}
          <Tooltip
            cursor={{ stroke: CHROME[mode].axis, strokeWidth: 1 }}
            content={tipContent(mode, "line")}
            isAnimationActive={false}
          />
          {single ? (
            <Area
              type="monotone"
              dataKey={drawn[0].name}
              name={drawn[0].name}
              stroke={colors.get(drawn[0].name)}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill={colors.get(drawn[0].name)}
              fillOpacity={0.1}
              dot={false}
              activeDot={{ r: 4, strokeWidth: 2, stroke: SURFACE[mode] }}
              connectNulls={false}
              isAnimationActive={false}
              label={showEndLabels ? endLabel(ends[0], mode, full) : false}
            />
          ) : (
            drawn.map((s, i) => (
              <Line
                key={s.name}
                type="monotone"
                dataKey={s.name}
                name={s.name}
                stroke={colors.get(s.name)}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                dot={false}
                activeDot={{ r: 4, strokeWidth: 2, stroke: SURFACE[mode] }}
                connectNulls={false}
                isAnimationActive={false}
                label={showEndLabels ? endLabel(ends[i], mode, (v) => clip(s.name, 12), true) : false}
              />
            ))
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

/**
 * Endpoint label renderer. A single series gets its final value; two to four get
 * their names, because with more than one line identity is the scarce channel.
 * The text wears an ink token, never the series colour — a light categorical hue
 * is illegible as text on either surface.
 */
function endLabel(
  end: { name: string; index: number; value: number } | null,
  mode: Mode,
  text: (v: number) => string,
  secondary = false,
) {
  return (props: unknown): ReactElement => {
    const p = props as { x?: number; y?: number; index?: number };
    if (!end || p.index !== end.index || p.x == null || p.y == null) return <g />;
    return (
      <text
        x={p.x + 8}
        y={p.y + 4}
        fill={secondary ? INK[mode].secondary : INK[mode].primary}
        fontSize={11}
        fontWeight={secondary ? 500 : 600}
      >
        {text(end.value)}
      </text>
    );
  };
}

// ============================================================ bar chart

export interface BarDatum {
  label: string;
  value: number;
}
export interface BarChartCardProps {
  title: string;
  data: BarDatum[];
  horizontal?: boolean;
  note?: string;
  yLabel?: string;
}

/**
 * One measure across nominal categories. One series means one colour for every
 * bar — colouring darker-where-bigger would double-encode the bar length and burn
 * the only free channel.
 */
export function BarChartCard({ title, data, horizontal, note, yLabel }: BarChartCardProps) {
  const mode = useMode();
  const color = SERIES[mode][0];
  const H = horizontal ? Math.max(180, data.length * 34 + 48) : 260;
  // Values at the tip stop working once they crowd; past a dozen bars the axis
  // and the tooltip carry them.
  const tips = data.length > 0 && data.length <= 12;

  if (data.length === 0) {
    return (
      <ChartFrame title={title} note={note}>
        <NoData height={220} body="Nothing matched the current filters." />
      </ChartFrame>
    );
  }

  const catWidth = Math.min(160, Math.max(64, Math.max(...data.map((d) => d.label.length)) * 6.5));

  return (
    <ChartFrame title={title} note={note}>
      <ResponsiveContainer width="100%" height={H}>
        <BarChart
          data={data}
          layout={horizontal ? "vertical" : "horizontal"}
          margin={{ top: 12, right: horizontal && tips ? 52 : 12, bottom: 4, left: yLabel ? 8 : 0 }}
        >
          <CartesianGrid {...gridProps(mode, !!horizontal)} />
          {horizontal ? (
            <>
              <XAxis
                type="number"
                tick={axisTick(mode)}
                tickLine={false}
                axisLine={axisLine(mode)}
                tickFormatter={compact}
              />
              <YAxis
                type="category"
                dataKey="label"
                tick={axisTick(mode)}
                tickLine={false}
                axisLine={axisLine(mode)}
                width={catWidth}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey="label"
                tick={axisTick(mode)}
                tickLine={false}
                axisLine={axisLine(mode)}
                interval={0}
                minTickGap={4}
              />
              <YAxis
                tick={axisTick(mode)}
                tickLine={false}
                axisLine={false}
                width={48}
                domain={[0, "auto"]}
                tickFormatter={compact}
                label={yAxisLabel(yLabel, mode)}
              />
            </>
          )}
          {/* On bars the mark is the hit target, so the cursor is a wash rather
              than a crosshair. */}
          <Tooltip
            cursor={{ fill: CHROME[mode].border }}
            content={tipContent(mode, "rect")}
            isAnimationActive={false}
          />
          <Bar
            dataKey="value"
            name={title}
            fill={color}
            maxBarSize={24}
            radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
            isAnimationActive={false}
          >
            {tips && (
              <LabelList
                dataKey="value"
                position={horizontal ? "right" : "top"}
                fill={INK[mode].secondary}
                fontSize={11}
                formatter={(v: unknown) => full(Number(v))}
              />
            )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

// ============================================================ stacked bar

export interface StackedRow {
  label: string;
  [category: string]: string | number;
}
export interface StackedBarCardProps {
  title: string;
  data: StackedRow[];
  categories: string[];
  note?: string;
  yLabel?: string;
}

/** Composition over time or across buckets. Categories keep their colour across filters. */
export function StackedBarCard({ title, data, categories, note, yLabel }: StackedBarCardProps) {
  const mode = useMode();
  const H = 260;
  const colors = new Map(categories.map((c) => [c, seriesColor(c, mode)]));

  if (data.length === 0 || categories.length === 0) {
    return (
      <ChartFrame title={title} note={note}>
        <NoData height={H} body="Nothing matched the current filters." />
      </ChartFrame>
    );
  }

  const legend: LegendEntry[] = categories.map((c) => ({ label: c, color: colors.get(c)! }));

  return (
    <ChartFrame title={title} note={note} legend={legend} legendShape="rect">
      <ResponsiveContainer width="100%" height={H}>
        <BarChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: yLabel ? 8 : 0 }}>
          <CartesianGrid {...gridProps(mode, false)} />
          <XAxis
            dataKey="label"
            tick={axisTick(mode)}
            tickLine={false}
            axisLine={axisLine(mode)}
            minTickGap={12}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={axisTick(mode)}
            tickLine={false}
            axisLine={false}
            width={48}
            domain={[0, "auto"]}
            tickFormatter={compact}
            label={yAxisLabel(yLabel, mode)}
          />
          <Tooltip
            cursor={{ fill: CHROME[mode].border }}
            content={tipContent(mode, "rect")}
            isAnimationActive={false}
          />
          {categories.map((c, i) => (
            <Bar
              key={c}
              dataKey={c}
              name={c}
              stackId="s"
              fill={colors.get(c)}
              maxBarSize={24}
              // A 2px stroke in the surface colour is how you cut the 2px gap
              // between touching segments in SVG — it separates with surface, not
              // with an outline drawn around the mark.
              stroke={SURFACE[mode]}
              strokeWidth={2}
              radius={i === categories.length - 1 ? [4, 4, 0, 0] : 0}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

// ============================================================ donut

export interface DonutCardProps {
  title: string;
  data: BarDatum[];
  note?: string;
  centerCaption?: string;
}

const OTHER = "Other";

/**
 * Part-to-whole at a glance. Capped at six visible slices — past that adjacent
 * classes blur and the tail folds into "Other" rather than inventing a ninth hue.
 */
export function DonutCard({ title, data, note, centerCaption = "total" }: DonutCardProps) {
  const mode = useMode();
  const H = 240;

  const slices = useMemo(() => {
    const sorted = [...data].filter((d) => d.value > 0).sort((a, b) => b.value - a.value);
    if (sorted.length <= 6) return sorted;
    const head = sorted.slice(0, 5);
    const tail = sorted.slice(5).reduce((s, d) => s + d.value, 0);
    return [...head, { label: OTHER, value: tail }];
  }, [data]);

  const total = slices.reduce((s, d) => s + d.value, 0);

  if (slices.length === 0 || total === 0) {
    return (
      <ChartFrame title={title} note={note}>
        <NoData height={H} body="Nothing to break down yet." />
      </ChartFrame>
    );
  }

  // "Other" is a residue, not an entity, so it takes a muted ink rather than a
  // categorical slot it would then hold hostage.
  const colorOf = (label: string) => (label === OTHER ? INK[mode].muted : seriesColor(label, mode));

  const legend: LegendEntry[] = slices.map((d) => ({
    label: d.label,
    color: colorOf(d.label),
    value: `${full(d.value)} · ${pct(d.value / total)}`,
  }));

  return (
    <ChartFrame title={title} note={note} legend={legend} legendShape="rect">
      <div className="chart-plot">
        <ResponsiveContainer width="100%" height={H}>
          <PieChart>
            <Tooltip
              content={tipContent(mode, "rect")}
              isAnimationActive={false}
            />
            <Pie
              data={slices}
              dataKey="value"
              nameKey="label"
              innerRadius="58%"
              outerRadius="82%"
              paddingAngle={1}
              stroke={SURFACE[mode]}
              strokeWidth={2}
              isAnimationActive={false}
            >
              {slices.map((d) => (
                <Cell key={d.label} fill={colorOf(d.label)} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="donut-center">
          <div>
            <div className="total">{compact(total)}</div>
            <div className="cap">{centerCaption}</div>
          </div>
        </div>
      </div>
    </ChartFrame>
  );
}

// ============================================================ sparkline

export interface SparklineProps {
  points: number[];
  height?: number;
  color?: string;
}

/** Shape only — no axes, no tooltip. The stat tile beside it carries the number. */
export function Sparkline({ points, height = 32, color }: SparklineProps) {
  const mode = useMode();
  const stroke = color ?? SERIES[mode][0];
  const rows = useMemo(() => points.map((y, i) => ({ i, y })), [points]);

  if (points.length < 2) return <div className="spark" style={{ height }} />;

  const last = rows.length - 1;
  const dot = (props: unknown): ReactElement => {
    const p = props as { cx?: number; cy?: number; index?: number };
    if (p.index !== last || p.cx == null || p.cy == null) return <g />;
    // 2px surface ring so the end dot stays legible where it crosses the line.
    return <circle cx={p.cx} cy={p.cy} r={2.5} fill={stroke} stroke={SURFACE[mode]} strokeWidth={2} />;
  };

  return (
    <div className="spark" style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={rows} margin={{ top: 4, right: 4, bottom: 2, left: 2 }}>
          <YAxis hide domain={["dataMin", "dataMax"]} />
          <Line
            type="monotone"
            dataKey="y"
            stroke={stroke}
            strokeWidth={1.5}
            strokeOpacity={0.65}
            strokeLinecap="round"
            dot={dot}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ============================================================ stat tile

export interface StatTileProps {
  label: string;
  value: number | string;
  unit?: string;
  /** Signed fractional change, e.g. 0.124 for +12.4%. */
  delta?: number;
  /** Set when down is the good direction (latency, error rate, cost). */
  invert?: boolean;
  deltaLabel?: string;
  spark?: number[];
}

/**
 * The form for a single current value. The delta carries an arrow glyph as well
 * as a colour, so direction survives both colour blindness and a grayscale print.
 */
export function StatTile({ label, value, unit, delta, invert, deltaLabel, spark }: StatTileProps) {
  const shown = typeof value === "number" ? compact(value) : value;
  const flat = delta === undefined || delta === 0 || !Number.isFinite(delta);
  const rising = !flat && (delta as number) > 0;
  const good = invert ? !rising : rising;
  const tone = flat ? "flat" : good ? "up" : "down";
  const glyph = flat ? "→" : rising ? "▲" : "▼";

  return (
    <div className="stat">
      <div className="label">{label}</div>
      {/* Proportional figures: tabular-nums gives every digit a zero's width and
          reads loose at display size. Tabular is for columns, not headlines. */}
      <div className="value">
        {shown}
        {unit && <span className="unit">{unit}</span>}
      </div>
      {delta !== undefined && Number.isFinite(delta) && (
        <div className={`delta ${tone}`}>
          <span aria-hidden="true">{glyph}</span>
          <span>
            {rising && !flat ? "+" : ""}
            {pct(Math.abs(delta) === 0 ? 0 : delta)}
          </span>
          {deltaLabel && <span className="muted">{deltaLabel}</span>}
        </div>
      )}
      {spark && spark.length > 1 && <Sparkline points={spark} />}
    </div>
  );
}

// ============================================================ heatmap

export interface HeatmapProps {
  rows: string[];
  cols: string[];
  /** values[rowIndex][colIndex]. */
  values: number[][];
  title?: string;
  note?: string;
  unit?: string;
}

interface HoverCell {
  row: string;
  col: string;
  value: number;
  x: number;
  y: number;
}

/**
 * Magnitude across a grid. One hue, light to dark, with a scale legend — a
 * rainbow ramp would make the reader decode hue order before reading a value.
 */
export function Heatmap({ rows, cols, values, title, note, unit }: HeatmapProps) {
  const mode = useMode();
  const [hover, setHover] = useState<HoverCell | null>(null);

  const flat = values.flat().filter((v) => Number.isFinite(v));
  const min = flat.length ? Math.min(...flat) : 0;
  const max = flat.length ? Math.max(...flat) : 0;
  const range = max - min || 1;

  if (rows.length === 0 || cols.length === 0) {
    return (
      <ChartFrame title={title} note={note}>
        <NoData height={180} body="No grid to draw for this slice." />
      </ChartFrame>
    );
  }

  const grid: CSSProperties = {
    gridTemplateColumns: `auto repeat(${cols.length}, minmax(28px, 1fr))`,
  };

  return (
    <ChartFrame title={title} note={note}>
      <div className="chart-plot" onMouseLeave={() => setHover(null)}>
        <div className="heat" style={grid}>
          <div />
          {cols.map((c) => (
            <div className="collab" key={c} title={c}>
              {clip(c, 8)}
            </div>
          ))}
          {rows.map((r, ri) => (
            <Fragment key={r}>
              <div className="rowlab" title={r}>
                {clip(r, 22)}
              </div>
              {cols.map((c, ci) => {
                const v = values[ri]?.[ci];
                const known = Number.isFinite(v);
                return (
                  <div
                    key={`${r}/${c}`}
                    className="cell"
                    tabIndex={0}
                    role="img"
                    aria-label={`${r}, ${c}: ${known ? full(v) : "no data"}`}
                    style={{ background: known ? rampAt((v - min) / range) : CHROME[mode].grid }}
                    onMouseEnter={(e) => {
                      if (!known) return;
                      const el = e.currentTarget;
                      setHover({ row: r, col: c, value: v, x: el.offsetLeft + el.offsetWidth / 2, y: el.offsetTop });
                    }}
                    onFocus={(e) => {
                      if (!known) return;
                      const el = e.currentTarget;
                      setHover({ row: r, col: c, value: v, x: el.offsetLeft + el.offsetWidth / 2, y: el.offsetTop });
                    }}
                    onBlur={() => setHover(null)}
                  />
                );
              })}
            </Fragment>
          ))}
        </div>
        {hover && (
          <motion.div
            className="tooltip float"
            style={{ left: hover.x, top: hover.y }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.09 }}
          >
            <div className="t-label">
              {hover.row} · {hover.col}
            </div>
            <div className="t-row">
              <span className="val">
                {full(hover.value)}
                {unit ? ` ${unit}` : ""}
              </span>
            </div>
          </motion.div>
        )}
      </div>
      {/* A sequential fill is only readable with its scale printed beside it. */}
      <div className="scale">
        <span>{full(min)}</span>
        {BLUE_RAMP.map((c) => (
          <span className="step" key={c} style={{ background: c }} />
        ))}
        <span>
          {full(max)}
          {unit ? ` ${unit}` : ""}
        </span>
      </div>
    </ChartFrame>
  );
}

// ============================================================ funnel

export interface FunnelStage {
  label: string;
  count: number;
}
export interface FunnelChartProps {
  stages: FunnelStage[];
  title?: string;
  note?: string;
}

interface FunnelRow extends FunnelStage {
  ofStart: number;
  ofPrev: number;
  dropped: number;
}

/**
 * Ordered stages, drawn as ranked horizontal bars rather than a tapering
 * trapezoid — a trapezoid encodes the value in an area nobody can compare.
 * Ordered categories take the ordinal ramp, not eight categorical hues.
 */
export function FunnelChart({ stages, title, note }: FunnelChartProps) {
  const mode = useMode();

  const rows = useMemo<FunnelRow[]>(() => {
    const start = stages[0]?.count ?? 0;
    return stages.map((s, i) => {
      const prev = i === 0 ? s.count : stages[i - 1].count;
      return {
        ...s,
        ofStart: start > 0 ? s.count / start : 0,
        ofPrev: prev > 0 ? s.count / prev : 0,
        dropped: Math.max(0, prev - s.count),
      };
    });
  }, [stages]);

  if (rows.length === 0) {
    return (
      <ChartFrame title={title} note={note}>
        <NoData height={180} body="No stage counts for this window." />
      </ChartFrame>
    );
  }

  const ramp = ORDINAL_RAMP[mode];
  const stepColor = (i: number) =>
    ramp[rows.length === 1 ? ramp.length - 1 : Math.round((i / (rows.length - 1)) * (ramp.length - 1))];

  const H = Math.max(180, rows.length * 34 + 48);
  const labelWidth = Math.min(170, Math.max(72, Math.max(...rows.map((r) => r.label.length)) * 6.5));

  const funnelTip = (p: unknown): ReactElement | null => {
    const t = p as TipProps & { payload?: Array<TipItem & { payload?: FunnelRow }> };
    if (!t.active || !t.payload || t.payload.length === 0) return null;
    const row = t.payload[0].payload;
    if (!row) return null;
    return (
      <motion.div className="tooltip" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.09 }}>
        <div className="t-label">{row.label}</div>
        <div className="t-row">
          <span>Reached</span>
          <span className="val">{full(row.count)}</span>
        </div>
        <div className="t-row">
          <span>Of start</span>
          <span className="val">{pct(row.ofStart)}</span>
        </div>
        <div className="t-row">
          <span>Of previous</span>
          <span className="val">{pct(row.ofPrev)}</span>
        </div>
        <div className="t-row">
          <span>Dropped here</span>
          <span className="val">{full(row.dropped)}</span>
        </div>
      </motion.div>
    );
  };

  return (
    <ChartFrame title={title} note={note}>
      <ResponsiveContainer width="100%" height={H}>
        <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 96, bottom: 4, left: 0 }}>
          <CartesianGrid {...gridProps(mode, true)} />
          <XAxis
            type="number"
            tick={axisTick(mode)}
            tickLine={false}
            axisLine={axisLine(mode)}
            tickFormatter={compact}
          />
          <YAxis
            type="category"
            dataKey="label"
            tick={axisTick(mode)}
            tickLine={false}
            axisLine={axisLine(mode)}
            width={labelWidth}
          />
          <Tooltip cursor={{ fill: CHROME[mode].border }} content={funnelTip} isAnimationActive={false} />
          <Bar dataKey="count" name="Reached" maxBarSize={24} radius={[0, 4, 4, 0]} isAnimationActive={false}>
            {rows.map((r, i) => (
              <Cell key={r.label} fill={stepColor(i)} />
            ))}
            <LabelList
              dataKey="count"
              position="right"
              fill={INK[mode].secondary}
              fontSize={11}
              formatter={(v: unknown) => {
                const n = Number(v);
                const start = rows[0].count;
                return start > 0 ? `${full(n)} · ${pct(n / start)}` : full(n);
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}
